/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { getRecyclingAdjustmentTotal, isRecyclingAdjustmentByLabel } from '@/lib/cart/recycling-fee';

/**
 * Cart / confirmation subtotal shown to shoppers:
 * product subtotal minus protection-plan and recycling-fee line items, which
 * are rendered in their own rows.
 */
export function getDisplayedMerchandiseSubtotal(
    productSubTotalRaw: number,
    protectionPlanTotal: number,
    stateRecyclingFeeTotal: number
): number {
    return Math.max(0, productSubTotalRaw - protectionPlanTotal - stateRecyclingFeeTotal);
}

/**
 * Recompose the displayed order total from visible rows so UI math always
 * reconciles with rendered lines.
 */
export function getRecomposedOrderTotal({
    merchandiseSubtotal,
    orderAdjustmentTotal = 0,
    shippingTotal,
    protectionPlanTotal,
    stateRecyclingFeeTotal = 0,
    taxTotal,
}: {
    merchandiseSubtotal: number;
    orderAdjustmentTotal?: number;
    shippingTotal: number;
    protectionPlanTotal: number;
    stateRecyclingFeeTotal?: number;
    taxTotal: number;
}): number {
    return (
        merchandiseSubtotal +
        orderAdjustmentTotal +
        shippingTotal +
        protectionPlanTotal +
        stateRecyclingFeeTotal +
        taxTotal
    );
}

const MONEY_EPSILON = 0.02;

function roundMoney(value: number): number {
    return Math.round(value * 100) / 100;
}

function amountsMatch(a: number, b: number): boolean {
    return Math.abs(a - b) <= MONEY_EPSILON;
}

type MerchandiseLineItem = {
    price?: number | null;
    basePrice?: number | null;
    quantity?: number | null;
    priceAfterItemDiscount?: number | null;
    priceAdjustments?: ReadonlyArray<{
        price?: number | null;
        itemText?: string;
        calloutMsg?: string;
        promotionId?: string;
        priceAdjustmentId?: string;
        c_isRecyclingFee?: unknown;
    }>;
};

/**
 * Line total that feeds the confirmation / cart merchandise subtotal:
 * after item-level promotions, excluding recycling fees (those have their
 * own summary row) and before order-level promotions.
 *
 * Shopper Orders often returns `price` as the unadjusted XML `<net-price>`.
 * When that matches `basePrice × quantity`, item promotions on
 * `priceAdjustments` are applied so kit roll-ups stay in lock-step with
 * standalone rows and the Order Summary subtotal.
 */
export function getDisplayedItemLineTotal(item: MerchandiseLineItem): number {
    const qty = item.quantity ?? 1;
    const adjustments = (item.priceAdjustments ?? []).map((adj) => ({
        price: adj.price ?? undefined,
        itemText: adj.itemText,
        calloutMsg: adj.calloutMsg,
        promotionId: adj.promotionId,
        priceAdjustmentId: adj.priceAdjustmentId,
        c_isRecyclingFee: adj.c_isRecyclingFee,
    }));
    const recycling = getRecyclingAdjustmentTotal({ priceAdjustments: adjustments });
    const promoTotal = adjustments
        .filter((adj) => !isRecyclingAdjustmentByLabel(adj))
        .reduce((sum, adj) => sum + (adj.price ?? 0), 0);

    if (typeof item.priceAfterItemDiscount === 'number') {
        return roundMoney(Math.max(0, item.priceAfterItemDiscount - recycling));
    }

    const baseLine = typeof item.basePrice === 'number' ? roundMoney(item.basePrice * qty) : null;

    if (typeof item.price === 'number') {
        if (baseLine != null && amountsMatch(item.price, baseLine)) {
            return roundMoney(Math.max(0, baseLine + promoTotal));
        }
        return roundMoney(Math.max(0, item.price - recycling));
    }

    if (baseLine != null) {
        return roundMoney(Math.max(0, baseLine + promoTotal));
    }
    return 0;
}

/**
 * Pre-discount (list) shipping total: net `shippingTotal` plus the absolute
 * value of shipping-line adjustments. Adjustments are negative, so
 * `net - sum(adjustments)` restores the original method fee.
 */
export function getPreDiscountShippingTotal(
    netShipping: number,
    adjustments: ReadonlyArray<{ price?: number | null }>
): number {
    const adjustmentTotal = adjustments.reduce((sum, adj) => sum + (adj.price ?? 0), 0);
    return roundMoney(netShipping - adjustmentTotal);
}

export interface DeliveryMethodTotals {
    hdTotal: number;
    dsTotal: number;
    whiteGloveTotal: number;
    hasHdItems: boolean;
    hasDsItems: boolean;
    hasDs3plItems: boolean;
}

/**
 * Restore pre-discount per-method shipping amounts for confirmation display.
 *
 * Cart `c_priceDetails.netPrice` is written before shipping promos. At order
 * placement, `fillShippingPriceDetailsGross` overwrites those nets with the
 * post-discount share so APG can prorate `grossPrice`. Confirmation would
 * otherwise show the discount line AND already-discounted method rows.
 *
 * Returns `null` when the original split cannot be recovered (free-shipping
 * promo on a mixed-method order) — the caller should fall back to a single
 * aggregate row showing `preDiscountShipping`.
 */
export function restorePreDiscountDeliveryBreakdown<T extends DeliveryMethodTotals>(
    breakdown: T,
    netShipping: number,
    preDiscountShipping: number
): T | null {
    if (preDiscountShipping <= netShipping) {
        return breakdown;
    }

    const methodSum = roundMoney(breakdown.hdTotal + breakdown.dsTotal + breakdown.whiteGloveTotal);

    if (amountsMatch(methodSum, preDiscountShipping)) {
        return breakdown;
    }

    if (netShipping > 0 && amountsMatch(methodSum, netShipping)) {
        const ratio = preDiscountShipping / netShipping;
        const hdTotal = roundMoney(breakdown.hdTotal * ratio);
        const dsTotal = roundMoney(breakdown.dsTotal * ratio);
        const whiteGloveTotal = roundMoney(breakdown.whiteGloveTotal * ratio);
        const restored = { ...breakdown, hdTotal, dsTotal, whiteGloveTotal };
        const remainder = roundMoney(preDiscountShipping - roundMoney(hdTotal + dsTotal + whiteGloveTotal));
        if (remainder === 0) {
            return restored;
        }
        // Park rounding remainder on the largest restored bucket so the
        // visible method rows still sum to the original shipping fee.
        if (hdTotal >= dsTotal && hdTotal >= whiteGloveTotal) {
            restored.hdTotal = roundMoney(hdTotal + remainder);
        } else if (dsTotal >= whiteGloveTotal) {
            restored.dsTotal = roundMoney(dsTotal + remainder);
        } else {
            restored.whiteGloveTotal = roundMoney(whiteGloveTotal + remainder);
        }
        return restored;
    }

    if (methodSum === 0 && preDiscountShipping > 0) {
        const presentCount =
            Number(breakdown.hasHdItems) + Number(breakdown.hasDsItems) + Number(breakdown.hasDs3plItems);
        if (presentCount === 1) {
            return {
                ...breakdown,
                hdTotal: breakdown.hasHdItems ? preDiscountShipping : 0,
                dsTotal: breakdown.hasDsItems ? preDiscountShipping : 0,
                whiteGloveTotal: breakdown.hasDs3plItems ? preDiscountShipping : 0,
            };
        }
        return null;
    }

    return breakdown;
}
